import React, { useState } from "react";

import { getFileUrl } from "../core/config/api";
import NotificationIcon from "./NotificationIcon";
import { timeAgo, watchLabel } from "./notificationPresentation";

/**
 * One notification row, shared by the bell popover and the /notifications page.
 *
 * Layout is a fixed three-column grid — thumbnail | text | time — so a list of
 * 30 rows has one straight left edge and one straight right edge instead of
 * the ragged, overlapping blocks the first release produced.
 *
 * The thumbnail prefers the real watch photo (data.image_file_id, emitted by
 * the backend as a FILE ID) and falls back to a per-event icon, so people /
 * catalogue / bulk-summary rows still read as something rather than a hole.
 */
const NotificationItem = ({ item, onOpen, compact = false }) => {
  const [imageFailed, setImageFailed] = useState(false);

  const data = item.data || {};
  const watch = watchLabel(data);
  // Only show the watch line when the TITLE does not already carry it. The
  // backend now leads every title with the subject ("1 new match · Rolex
  // Datejust · 126334-0006"), so repeating it underneath duplicated the row
  // and — at this density — read as if it belonged to the NEXT notification.
  const watchIsRedundant = !!watch && (item.title || "").includes(watch);
  const imageUrl = !imageFailed && data.image_file_id ? getFileUrl(data.image_file_id) : null;

  return (
    <button
      type="button"
      className={[
        "rc-note",
        compact ? "rc-note--compact" : "",
        item.isRead ? "" : "rc-note--unread",
      ].filter(Boolean).join(" ")}
      onClick={() => onOpen(item)}
    >
      <span className="rc-note__media" aria-hidden="true">
        {imageUrl ? (
          <img src={imageUrl} alt="" loading="lazy" onError={() => setImageFailed(true)} />
        ) : (
          <span className="rc-note__icon"><NotificationIcon eventType={item.eventType} /></span>
        )}
      </span>

      <span className="rc-note__body">
        <span className="rc-note__title">{item.title}</span>
        {item.body ? <span className="rc-note__text">{item.body}</span> : null}
        {watch && !compact && !watchIsRedundant ? (
          <span className="rc-note__meta">{watch}</span>
        ) : null}
      </span>

      <span className="rc-note__aside">
        <span className="rc-note__time">{timeAgo(item.createdAt)}</span>
        {item.isRead ? null : <span className="rc-note__dot" aria-label="Unread" />}
      </span>
    </button>
  );
};

export default NotificationItem;
